/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {StepCountUpBigWinController} from "@nolimitcity/slot-game/bin/game/winpresentation/bigwin/stepcountup/StepCountUpBigWinController";
import {Timeline, TimelineLite} from "gsap";
import {Fruits} from "../../Fruits";
import {FruitsGameConfig} from "../../FruitsGameConfig";
import {FruitsGameModuleConfig} from "../../FruitsGameModuleConfig";
import {FruitsSoundConfig} from "../../FruitsSoundConfig";
import {FruitsGameMode} from "../../gamemode/FruitsGameMode";
import {FruitsReels} from "../../reel/reelarea/FruitsReels";
import {FruitsReelSymbol} from "../../reelsymbol/FruitsReelSymbol";
import {FruitsStickyRespinOverlayView} from "../../screen/respin/FruitsStickyRespinOverlayView";
import {FruitsParsedGameData} from "../../server/data/FruitsParsedGameData";
import {FruitsBigWinView} from "./FruitsBigWinView";

export class FruitsBigWinController extends StepCountUpBigWinController {

    private _gameView:FruitsBigWinView;
    private _endingTimeline:TimelineLite | null;
    private _abortHandled:boolean = false;
    private _orientation:Orientation;

    constructor(indexes:[number, number]) {
        super(indexes, {skipLastLevelCountUpIfSmall : true});

        this._gameView = new FruitsBigWinView();
        EventHandler.addEventListener(this, FruitsReels.SET_ORIENTATION, (event:GameEvent) => this._orientation = event.params[0]);
    }

    protected parseFeatureGameData(data:FruitsParsedGameData):void {
        super.parseFeatureGameData(data);
        this._gameData = data;
    }

    protected hasWin():boolean {
        if(this._gameData != null && this._gameData!.mode === FruitsGameMode.BONUS_GAME) {
            return false;
        }
        return (this._gameData != null && this._gameData.isBigWin);
    }


    protected getBigWinLevelAnimation(level:number):Timeline {
        return this._gameView.getBigWinLevelAnimation(level, this._winRatios[level]);
    }

    protected playBigWinCountUp(level:number, duration:number):Timeline | null {
        this._abortHandled = false;
        const preTotalWin:number = this._gameData!.totalWin - this._gameData!.singleWin;
        const fromValue:number = level === 0 ? 0 : this._gameData!.playedBetValue * GameConfig.instance.WIN_RATIOS.bigWin[level].ratio;
        const toValue:number = level === this._winLevel ? this._gameData!.singleWin : this._gameData!.playedBetValue * GameConfig.instance.WIN_RATIOS.bigWin[level + 1].ratio;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            this.playCountUp(fromValue, toValue, duration, level, preTotalWin),
            () => this.playBigWinCountUpSound(level)
        ]);

        return timeline;
    }

    protected playBigWinLevelAnimation(currentLevel:number):void {
        this._gameView._particles.prepareParticleEmitter();
        if(currentLevel <= this._winLevel) {
            const levelAnimation:Timeline = this.getBigWinLevelAnimation(currentLevel);
            this._currentTimeline = new TimelineLite({paused : true});
            let delay = 0;
            if(currentLevel === 0) {
                for(let i:number = 0; i < FruitsGameConfig.instance.REELS_NUM; i++) {
                    let sym:FruitsReelSymbol = <FruitsReelSymbol>Reels.getSymbol(i, 0);
                    if(sym) {
                        sym.changeState({
                            state : FruitsGameConfig.instance.SYMBOL_STATES.normal,
                            fadeInDuration : 0.15,
                            setStack : false
                        })
                    }
                }
                this._currentTimeline.add([
                    this.getBetLineAnimation(),
                    this._gameView.bgAnimation(currentLevel, true),
                ]);
                SlotGame.sound.playEffect(FruitsSoundConfig.BIG_WIN_FADE_IN);
                delay = 1.6;
            }

            this._currentTimeline.add([
                this.playBigWinCountUp(currentLevel, GameConfig.instance.WIN_RATIOS.bigWin[currentLevel].duration),
                levelAnimation,
            ], delay);
            this._currentTimeline.add(() => {
                this.playBigWinLevelAnimation(currentLevel + 1);
            });
            this._currentTimeline.play();
        } else {
            this.finish();
        }
    }

    private getBetLineAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add(() => {
            EventHandler.dispatchEvent(new GameEvent(FruitsStickyRespinOverlayView.REMOVE_STICKY_SYMBOLS));
        });

        tl.add(Fruits.initialView.buildUpWinningLines(this._betWinsData as IBetLineWinData[]));

        return tl;
    }

    protected playCountUp(from:number, to:number, duration:number, level:number, preTotalWin:number = 0):Timeline | null {
        return this._gameView.playCountUp(from, to, duration, level);
    }


    protected resetFeature():void {
        this._gameView.resetBigWinView();
        this.resetTimelines();
        super.resetFeature();
    }

    protected playBigWinCountUpSound(level:number):void {
        if(level === 0) {
            SlotGame.sound.playEffect(FruitsSoundConfig.BIG_WIN);
            SlotGame.sound.fadeAmbience(0, 0);
        }
    };

    protected abortWinPresentation():void {
        if(!this._abortHandled) {
            this.handleAbort(true);
        }
    }

    private resetTimelines():void {
        if(this._currentTimeline && this._currentTimeline.isActive()) {
            this._currentTimeline.pause();
            this._currentTimeline.kill();
        }

        if(this._endingTimeline && this._endingTimeline.isActive()) {
            this._endingTimeline.pause();
            this._endingTimeline.kill();
        }

        this._currentTimeline = null;
        this._endingTimeline = null;
    }

    protected finish(isAborted:boolean = false):void {
        if(isAborted && !this._abortHandled) {
            this.handleAbort(isAborted);
            return;

        }
        Fruits.initialView.hideLines();

        this._endingTimeline = new TimelineLite();
        this._endingTimeline.add([
            this._gameView.playEndingAnimation(this._gameData!.singleWin, this._winRatios[this._winLevel], this._winLevel, isAborted),
            () => SlotGame.winFieldController.showWinField(this._gameData!.totalWin)
        ]);
        this._endingTimeline.add(() => super.finish(isAborted));
        if((<FruitsParsedGameData>this._gameData)!.isStickyRespinTriggeredThisSpin && this._gameData!.nextMode === FruitsGameMode.NORMAL)
            FruitsGameModuleConfig.logoAnimationBit.changeLogo(FruitsGameMode.NORMAL);
    }

    private handleAbort(isAborted:boolean):void {
        this._abortHandled = true;
        this.resetTimelines();
        Fruits.initialView.hideLines();
        this._gameView.resetBigWinView();
        this._gameView.playEndingAnimation(this._gameData!.singleWin, this._winRatios[this._winLevel], this._winLevel, isAborted);
        SlotGame.winFieldController.showWinField(this._gameData!.totalWin);

        if((<FruitsParsedGameData>this._gameData)!.isStickyRespinTriggeredThisSpin && this._gameData!.nextMode === FruitsGameMode.NORMAL)
            FruitsGameModuleConfig.logoAnimationBit.changeLogo(FruitsGameMode.NORMAL);
        super.finish(isAborted);
    }

    protected stopAllSounds():void {

    }
}